/** One Google account, with independently enabled capabilities and incremental consent. */
import { z } from 'zod';

export const GOOGLE_CONNECTION_FEATURES = ['drive', 'gmail', 'calendar'] as const;
export const GoogleConnectionFeatureSchema = z.enum(GOOGLE_CONNECTION_FEATURES);
export type GoogleConnectionFeature = z.infer<typeof GoogleConnectionFeatureSchema>;
/** Calendar is reserved in the contract; no Calendar capability is shipped by this change. */
export const GoogleEnabledFeatureSchema = z.enum(['drive', 'gmail']);
export type GoogleEnabledFeature = z.infer<typeof GoogleEnabledFeatureSchema>;
export const GoogleFeatureStateSchema = z
  .object({
    state: z.enum(['ready', 'disabled', 'consent_required', 'unavailable']),
    available: z.boolean(),
  })
  .strict();
export const GoogleConnectionStatusSchema = z
  .object({
    success: z.literal(true),
    provider: z.literal('google'),
    state: z.enum(['not_configured', 'not_connected', 'connected', 'unavailable']),
    provider_email: z.string().nullable(),
    enabled_features: z.array(GoogleEnabledFeatureSchema),
    features: z
      .object({
        drive: GoogleFeatureStateSchema,
        gmail: GoogleFeatureStateSchema,
        calendar: z.object({ state: z.literal('unavailable'), available: z.literal(false) }).strict(),
      })
      .strict(),
  })
  .strict();
export type GoogleConnectionStatus = z.infer<typeof GoogleConnectionStatusSchema>;
export const GoogleAuthorizeBodySchema = z
  .object({
    feature: GoogleEnabledFeatureSchema,
    expected_email: z.string().email().max(320).optional(),
  })
  .strict();
export const GoogleAuthorizeResponseSchema = z
  .object({
    success: z.literal(true),
    authorization_url: z.string().url(),
    authorization_callback_uri: z.string().url(),
    state: z.string().min(1),
  })
  .strict();
export type GoogleAuthorizeResponse = z.infer<typeof GoogleAuthorizeResponseSchema>;
export const GoogleCompleteBodySchema = z
  .object({
    state: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    code: z.string().min(1).max(4096),
  })
  .strict();
export const GoogleReplacementBodySchema = z
  .object({
    state: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  })
  .strict();
const GoogleConnectedResponseSchema = z
  .object({
    success: z.literal(true),
    status: z.literal('connected'),
    provider: z.literal('google'),
    provider_email: z.string().email(),
  })
  .strict();
export const GoogleCompleteResponseSchema = z.discriminatedUnion('status', [
  GoogleConnectedResponseSchema,
  z
    .object({
      success: z.literal(true),
      status: z.literal('replacement_required'),
      state: z.string(),
      current_email: z.string(),
      incoming_email: z.string().email(),
    })
    .strict(),
]);
export type GoogleCompleteResponse = z.infer<typeof GoogleCompleteResponseSchema>;
export const GoogleFeatureUpdateBodySchema = z.object({ enabled: z.boolean() }).strict();
